import { useEffect, useRef } from 'react';
import {
  type ExtensionBridge,
  AppTokenCapability,
} from '@doxyme/extensions-bridge';
import {
  useAddCapability,
  useRegistrySingleton,
} from '@doxyme/extensions-bridge-react';
import { Specs } from '@doxyme/extensions-common';
import { useAuth } from '../../useAuth';
import { getLogger } from '../../../libs/logger';

const logger = getLogger('useExtensionsAppToken');

/**
 * Hook responsible for triggering registry api to retrieve app token for the given appId.
 *
 * When the host is not authenticated, the handler short-circuits without
 * forwarding to api-core. This both reduces the api-core 403 volume during
 * brief Frontegg refresh windows and gives extensions a structured
 * `HOST_UNAUTHENTICATED` failure reason so the population is countable in
 * telemetry separately from genuine api-core auth failures.
 */
export function useExtensionsAppToken(bridge: ExtensionBridge): void {
  const appToken = useAddCapability(bridge, () => new AppTokenCapability());
  const registry = useRegistrySingleton();
  const { isAuthenticated } = useAuth();

  // Mirror isAuthenticated through a ref so the effect's deps stay
  // [registry.apps]. Re-subscribing on every auth flip would briefly leave
  // the bridge with zero handlers, which can mis-resolve a Get request as
  // an empty token via AppTokenCapability.get's empty-handlers branch.
  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    const unsubscribeFromGet = appToken.onGet(
      async (manifestName: string): Promise<string> => {
        const app = registry.apps.find(
          (app) => app.manifest.name === manifestName,
        );
        if (!app) {
          throw new Error(`App with manifest name ${manifestName} not found`);
        }

        if (!isAuthenticatedRef.current) {
          logger.warn('App token requested while host is unauthenticated', {
            manifestName,
            appId: app.id,
          });
          throw Object.assign(
            new Error(
              `Cannot generate app token: host is unauthenticated (manifest=${manifestName})`,
            ),
            { code: Specs.AppToken.ErrorCode.HostUnauthenticated },
          );
        }

        return registry.generateAppToken(app.id);
      },
    );
    return () => {
      unsubscribeFromGet();
    };
  }, [registry.apps]);
}
